import React, { memo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, Tooltip } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles(theme => ({
  column: props => ({
    backgroundColor: props.color || '#ebecf0',
    borderRadius: 8,
    minWidth: 272,
    maxWidth: 272,
    height: '100%',
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    minHeight: 0,
  }),
  columnTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
    fontSize: '0.875rem',
    color: "#D6D6D6",
    minWidth: 0,
    flex: 1,
  },
  cardList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    ...theme.scrollbarStyles,
  },
  totalValue: {
    fontSize: '1rem',
    color: "#D6D6D6",
    fontWeight: 'bold',
  },
  columnHeader: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dragHandle: {
    cursor: 'grab',
    color: 'rgba(255, 255, 255, 0.7)',
    padding: theme.spacing(0.5),
    '&:hover': {
      color: 'rgba(255, 255, 255, 1)',
    },
  },
  columnContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  countBadge: {
    minWidth: 24,
    height: 24,
    padding: '0 7px',
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    // Fundo sempre claro: no dark mode text.primary é claro e sumia no balão
    color: theme.palette.getContrastText('#ffffff'),
    fontSize: '0.75rem',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
    boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
    lineHeight: 1,
  },
}));

const KanbanColumn = ({ id, title, tickets, color, index, updateTicket, onTicketClosed, onTicketTransferred, isAdmin }) => {
  const classes = useStyles({ color });

  const totalValue = tickets.reduce((acc, ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return acc + opportunityValue;
  }, 0);

  const ticketCount = tickets.length;

  return (
    <Draggable draggableId={id} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div className={classes.columnHeader}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
              <Tooltip
                title={i18n.t('kanban.laneTicketCountTooltip', { count: ticketCount })}
              >
                <span
                  className={classes.countBadge}
                  aria-label={i18n.t('kanban.laneTicketCountAria', { count: ticketCount })}
                >
                  {ticketCount}
                </span>
              </Tooltip>
              {isAdmin && (
                <div
                  {...provided.dragHandleProps}
                  className={classes.dragHandle}
                >
                  <DragIndicatorIcon fontSize="small" />
                </div>
              )}
              <Typography className={classes.columnTitle} noWrap>
                {title}
              </Typography>
            </div>
            <Typography className={classes.totalValue}>
              Total: R$ {totalValue.toFixed(2)}
            </Typography>
          </div>
          <Droppable droppableId={id} type="CARD">
            {(provided, snapshot) => (
              <div
                className={classes.cardList}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {tickets.map((ticket, index) => (
                  <KanbanCard
                    key={ticket.id}
                    ticket={ticket}
                    index={index}
                    updateTicket={updateTicket}
                    onTicketClosed={onTicketClosed}
                    onTicketTransferred={onTicketTransferred}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

const areKanbanColumnPropsEqual = (prevProps, nextProps) => {
  if (
    prevProps.id !== nextProps.id ||
    prevProps.title !== nextProps.title ||
    prevProps.color !== nextProps.color ||
    prevProps.index !== nextProps.index ||
    prevProps.isAdmin !== nextProps.isAdmin ||
    prevProps.updateTicket !== nextProps.updateTicket ||
    prevProps.onTicketClosed !== nextProps.onTicketClosed ||
    prevProps.onTicketTransferred !== nextProps.onTicketTransferred
  ) {
    return false;
  }

  const prevTickets = prevProps.tickets || [];
  const nextTickets = nextProps.tickets || [];

  if (prevTickets.length !== nextTickets.length) return false;

  for (let i = 0; i < prevTickets.length; i += 1) {
    if (prevTickets[i] !== nextTickets[i]) {
      return false;
    }
  }

  return true;
};

export default memo(KanbanColumn, areKanbanColumnPropsEqual);
